import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { teamMatchStats, noteColor, formatDateDE, teamWashStats } from "@/lib/matchStats";
import { positionAbbrev, positionColor } from "@/lib/playerConstants";
import { ArrowLeft, Trophy, TrendingDown, TrendingUp, BarChart3, Users, Shirt, AlertCircle } from "lucide-react";
import {
  LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip, ReferenceLine,
} from "recharts";
import { toast } from "sonner";

export default function MatchSeason() {
  const navigate = useNavigate();
  const [players, setPlayers] = useState([]);
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [p, m] = await Promise.all([api.get("/players"), api.get("/matches")]);
        setPlayers(p.data);
        setMatches(m.data);
      } catch { toast.error("Daten konnten nicht geladen werden"); }
      finally { setLoading(false); }
    })();
  }, []);

  const stats = useMemo(() => teamMatchStats(players, matches), [players, matches]);
  const wash = useMemo(() => teamWashStats(players, matches), [players, matches]);

  if (loading) return <div className="text-center py-20 text-zinc-500">Lädt…</div>;

  const chartData = stats.perMatchTeamAvg
    .filter((m) => m.teamAvg != null)
    .map((m) => ({
      datum: `${m.datum.slice(8,10)}.${m.datum.slice(5,7)}`,
      note: m.teamAvg,
      gegner: m.gegner,
    }));

  const trendDelta = stats.last3TeamAvg != null && stats.prev3TeamAvg != null
    ? stats.last3TeamAvg - stats.prev3TeamAvg
    : null;
  const trendIcon = trendDelta == null ? null : trendDelta < -0.1 ? "up" : trendDelta > 0.1 ? "down" : "stable";

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
      <button onClick={() => navigate("/noten")}
        className="text-xs text-zinc-400 hover:text-brand-lighter transition flex items-center gap-1 mb-5"
        data-testid="ms-back-btn">
        <ArrowLeft className="h-3 w-3" /> Zurück
      </button>

      <div className="mb-6">
        <div className="text-[11px] uppercase tracking-wider text-brand-lighter mb-1">Auswertung</div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-zinc-100">Saisonübersicht</h1>
        <p className="text-zinc-400 text-sm mt-1">Match-Bewertungen aller Spieler & Team-Entwicklung.</p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        <Stat label="Spiele" value={stats.totalMatches} testId="ms-matches" />
        <Stat label="Ø Team-Note" value={stats.teamOverallAvg == null ? "—" : stats.teamOverallAvg.toFixed(1)} color={noteColor(stats.teamOverallAvg)} testId="ms-team-avg" />
        <Stat label="Ø Starter" value={stats.starterAvg == null ? "—" : stats.starterAvg.toFixed(1)} color={noteColor(stats.starterAvg)} testId="ms-starter-avg" />
        <Stat label="Ø Ersatz" value={stats.ersatzAvg == null ? "—" : stats.ersatzAvg.toFixed(1)} color={noteColor(stats.ersatzAvg)} testId="ms-ersatz-avg" />
      </div>

      {/* Form curve */}
      <div className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-5 mb-6">
        <div className="flex items-center justify-between mb-3">
          <div className="text-[11px] uppercase tracking-wider text-zinc-400">Team-Leistungskurve</div>
          {trendIcon === "up" && (
            <div className="text-xs text-emerald-400 flex items-center gap-1"><TrendingUp className="h-3 w-3" /> Aufsteigend</div>
          )}
          {trendIcon === "down" && (
            <div className="text-xs text-rose-400 flex items-center gap-1"><TrendingDown className="h-3 w-3" /> Absteigend</div>
          )}
          {trendIcon === "stable" && (
            <div className="text-xs text-zinc-400">Stabil</div>
          )}
        </div>
        {chartData.length === 0 ? (
          <div className="text-sm text-zinc-500 py-8 text-center">Noch keine Bewertungen.</div>
        ) : (
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData} margin={{top: 10, right: 10, left: -20, bottom: 0}}>
                <XAxis dataKey="datum" stroke="#52525b" fontSize={11} />
                <YAxis domain={[1, 6]} reversed stroke="#52525b" fontSize={11} ticks={[1,2,3,4,5,6]} />
                <Tooltip
                  contentStyle={{ background: "#18181b", border: "1px solid #27272a", borderRadius: 4, fontSize: 12 }}
                  labelStyle={{ color: "#a1a1aa" }}
                  formatter={(value, _name, item) => [`${value.toFixed(1)} · vs. ${item.payload.gegner}`, "Note"]}
                />
                <ReferenceLine y={3.0} stroke="#3f3f46" strokeDasharray="3 3" />
                <Line type="monotone" dataKey="note" stroke="#3389c7" strokeWidth={2.5} dot={{ fill: "#3389c7", r: 4 }} activeDot={{ r: 6 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* Ranking */}
      <div className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="text-[11px] uppercase tracking-wider text-zinc-400">Spieler-Ranking nach Ø Note</div>
          <BarChart3 className="h-4 w-4 text-zinc-500" />
        </div>
        {stats.ranking.length === 0 ? (
          <div className="text-sm text-zinc-500">Noch keine Bewertungen.</div>
        ) : (
          <div className="space-y-1.5" data-testid="ms-ranking">
            {stats.ranking.map((row, i) => (
              <button key={row.player.id} onClick={() => navigate(`/spieler/${row.player.id}`)}
                className="w-full grid grid-cols-[32px_1fr_60px_60px] items-center gap-3 px-3 py-2 rounded-md hover:bg-zinc-800/40 text-left transition"
                data-testid={`ms-rank-${i}`}>
                <span className="font-display text-sm font-bold text-zinc-400 tabular-nums">{i + 1}.</span>
                <div className="min-w-0">
                  <div className="text-zinc-100 font-medium truncate">{row.player.name}</div>
                  <div className={"text-[11px] tracking-wide font-medium " + positionColor(row.player.position)}>
                    {positionAbbrev(row.player.position)} · {row.player.jahrgang}
                  </div>
                </div>
                <span className="text-xs text-zinc-400 text-right tabular-nums">{row.stats.total} Sp.</span>
                <span className={"font-display text-base font-bold text-right tabular-nums " + noteColor(row.stats.seasonAvg)} data-testid={`ms-rank-note-${i}`}>
                  {row.stats.seasonAvg == null ? "—" : row.stats.seasonAvg.toFixed(1)}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>
      {/* Wäsche-Ranking */}
      <div className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-5 mt-6" data-testid="wash-section">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <Shirt className="h-4 w-4 text-brand-lighter" />
            <div className="text-[11px] uppercase tracking-wider text-zinc-400">Wäsche-Ranking · Trikotwäsche</div>
          </div>
          {wash.diff > 1 && (
            <div className="text-xs text-amber-400 flex items-center gap-1" data-testid="wash-fairness-warning">
              <AlertCircle className="h-3 w-3" /> Wäschedienste sind aktuell ungleich verteilt.
            </div>
          )}
        </div>
        {wash.ranking.length === 0 ? (
          <div className="text-sm text-zinc-500">Noch keine Spieler im Kader.</div>
        ) : (
          <>
            <div className="grid grid-cols-3 gap-2 mb-4 pb-4 border-b border-zinc-800 text-center">
              <div>
                <div className="text-[10px] uppercase tracking-wide text-zinc-500">Min</div>
                <div className="font-display text-xl font-bold text-emerald-400 tabular-nums" data-testid="wash-min">{wash.min}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wide text-zinc-500">Max</div>
                <div className="font-display text-xl font-bold text-zinc-100 tabular-nums" data-testid="wash-max">{wash.max}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wide text-zinc-500">Differenz</div>
                <div className={"font-display text-xl font-bold tabular-nums " + (wash.diff > 1 ? "text-amber-400" : "text-zinc-100")} data-testid="wash-diff">{wash.diff}</div>
              </div>
            </div>
            <div className="space-y-1" data-testid="wash-ranking">
              {wash.ranking.map((row, i) => (
                <button key={row.player.id} onClick={() => navigate(`/spieler/${row.player.id}`)}
                  className="w-full grid grid-cols-[28px_1fr_70px_50px] items-center gap-3 px-3 py-1.5 rounded-md hover:bg-zinc-800/40 text-left transition"
                  data-testid={`wash-rank-${i}`}>
                  <span className="font-display text-xs font-bold text-zinc-400 tabular-nums">{i + 1}.</span>
                  <div className="min-w-0">
                    <div className="text-zinc-100 text-sm truncate">{row.player.name}</div>
                    <div className={"text-[10px] tracking-wide " + positionColor(row.player.position)}>
                      {positionAbbrev(row.player.position)} · {row.player.jahrgang}
                    </div>
                  </div>
                  <span className="text-[11px] text-zinc-500 text-right">
                    {row.lastDate ? row.lastDate.slice(8,10)+"."+row.lastDate.slice(5,7)+"."+row.lastDate.slice(2,4) : "—"}
                  </span>
                  <span className={"font-display text-base font-bold text-right tabular-nums " + (row.count === wash.min ? "text-emerald-400" : row.count === wash.max && wash.diff > 0 ? "text-amber-400" : "text-zinc-200")}>
                    {row.count}
                  </span>
                </button>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, color = "text-zinc-100", testId }) {
  return (
    <div className="rounded-md border border-zinc-800 bg-zinc-900/40 p-3">
      <div className="text-[10px] uppercase tracking-wide text-zinc-500">{label}</div>
      <div className={"font-display text-xl font-bold tabular-nums " + color} data-testid={testId}>{value}</div>
    </div>
  );
}
